import { useState, useEffect } from 'react';
import { Edit2, Trash2, Plus, Check, X, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '@/lib/api.js';
import { useToast } from '@/components/ui/Toast.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Input, FormField } from '@/components/ui/Input.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog.jsx';
import { DataTable } from '@/components/DataTable.jsx';

export default function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('view'); // view, edit, create
  const [expandedRole, setExpandedRole] = useState(null);
  const [formData, setFormData] = useState({ code: '', name: '', description: '' });
  const [selectedPerms, setSelectedPerms] = useState([]);
  const [searchPerm, setSearchPerm] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    loadRoles();
    loadPermissions();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const res = await api.get('/roles');
      setRoles(res);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const res = await api.get('/roles/all-permissions');
      setPermissions(res);
    } catch (e) {
      console.error('Failed to load permissions:', e);
    }
  };

  const handleNewRole = () => {
    setDialogMode('create');
    setFormData({ code: '', name: '', description: '' });
    setSelectedPerms([]);
    setSelectedRole(null);
    setShowDialog(true);
  };

  const handleEditRole = async (role) => {
    try {
      const fullRole = await api.get(`/roles/${role.id}`);
      setSelectedRole(fullRole);
      setDialogMode('edit');
      setFormData({ code: role.code, name: role.name, description: role.description || '' });
      setSelectedPerms(fullRole.permissions.map(p => p.id));
      setShowDialog(true);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'error' });
    }
  };

  const handleViewRole = async (role) => {
    try {
      const fullRole = await api.get(`/roles/${role.id}`);
      setSelectedRole(fullRole);
      setDialogMode('view');
      setShowDialog(true);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'error' });
    }
  };

  const handleSaveRole = async () => {
    try {
      if (!formData.name) {
        toast({ title: 'Validation', description: 'Name is required', variant: 'error' });
        return;
      }

      if (dialogMode === 'create') {
        if (!formData.code) {
          toast({ title: 'Validation', description: 'Code is required', variant: 'error' });
          return;
        }
        const newRole = await api.post('/roles', formData);
        
        // Add permissions
        if (selectedPerms.length > 0) {
          const add = selectedPerms;
          await api.post(`/roles/${newRole.id}/permissions/batch`, { add, remove: [] });
        }
        
        toast({ title: 'Success', description: 'Role created' });
        loadRoles();
      } else {
        // Update role
        await api.put(`/roles/${selectedRole.id}`, {
          name: formData.name,
          description: formData.description,
        });

        // Update permissions
        const currentPerms = selectedRole.permissions.map(p => p.id);
        const add = selectedPerms.filter(p => !currentPerms.includes(p));
        const remove = currentPerms.filter(p => !selectedPerms.includes(p));
        
        if (add.length > 0 || remove.length > 0) {
          await api.post(`/roles/${selectedRole.id}/permissions/batch`, { add, remove });
        }

        toast({ title: 'Success', description: 'Role updated' });
        loadRoles();
      }

      setShowDialog(false);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'error' });
    }
  };

  const handleDeleteRole = async (role) => {
    if (!confirm(`Delete role "${role.name}"? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/roles/${role.id}`);
      toast({ title: 'Success', description: 'Role deleted' });
      loadRoles();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'error' });
    }
  };

  const filteredPermissions = permissions.filter(p => 
    p.code.toLowerCase().includes(searchPerm.toLowerCase()) ||
    p.description.toLowerCase().includes(searchPerm.toLowerCase())
  );

  const permsByModule = filteredPermissions.reduce((acc, perm) => {
    if (!acc[perm.module]) acc[perm.module] = [];
    acc[perm.module].push(perm);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Roles</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400">Manage roles and their permissions</p>
        </div>
        <Button onClick={handleNewRole} className="gap-2">
          <Plus className="h-4 w-4" />
          New Role
        </Button>
      </div>

      {/* Roles List */}
      <div className="space-y-2">
        {roles.map(role => (
          <div key={role.id} className="border rounded-lg bg-white dark:bg-slate-900">
            <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800" onClick={() => setExpandedRole(expandedRole === role.id ? null : role.id)}>
              <div className="flex-1">
                <div className="font-medium text-slate-900 dark:text-white flex items-center gap-2">
                  {role.name}
                  {role.is_system && <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">System</span>}
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{role.description}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {role.permission_count} permission{role.permission_count !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {expandedRole === role.id ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleViewRole(role); }}>View</Button>
                {!role.is_system && (
                  <>
                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleEditRole(role); }}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleDeleteRole(role); }} className="text-red-600 dark:text-red-400">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Expanded Role Details */}
            {expandedRole === role.id && (
              <div className="border-t bg-slate-50 dark:bg-slate-800 p-4">
                <h4 className="font-medium text-sm mb-3">Permissions:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {role.permission_count === 0 ? (
                    <p className="text-sm text-slate-500">No permissions assigned</p>
                  ) : (
                    permissions
                      .filter(p => role.permissions?.some(rp => rp.id === p.id) || false)
                      .map(perm => (
                        <div key={perm.id} className="text-sm">
                          <code className="bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded text-xs">{perm.code}</code>
                          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">{perm.description}</p>
                        </div>
                      ))
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Dialog for Create/Edit/View */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent size="2xl">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'create' && 'Create New Role'}
              {dialogMode === 'edit' && 'Edit Role'}
              {dialogMode === 'view' && 'View Role'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 max-h-[60vh] overflow-y-auto">
            {/* Role Info */}
            {dialogMode !== 'view' && (
              <div className="space-y-4">
                <FormField label="Code" required disabled={dialogMode === 'edit'}>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="e.g., manager"
                    disabled={dialogMode === 'edit'}
                  />
                </FormField>
                <FormField label="Name" required>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Manager"
                  />
                </FormField>
                <FormField label="Description">
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Optional description"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md dark:bg-slate-800 dark:text-white text-sm"
                    rows={3}
                  />
                </FormField>
              </div>
            )}

            {/* Permissions Selection */}
            {dialogMode !== 'view' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Permissions
                </label>
                <Input
                  type="search"
                  placeholder="Search permissions..."
                  value={searchPerm}
                  onChange={(e) => setSearchPerm(e.target.value)}
                  className="mb-3"
                />

                <div className="space-y-4 max-h-96 overflow-y-auto border rounded p-3 bg-slate-50 dark:bg-slate-800">
                  {Object.entries(permsByModule).map(([module, perms]) => (
                    <div key={module}>
                      <h5 className="font-medium text-sm text-slate-900 dark:text-white mb-2 capitalize">{module}</h5>
                      <div className="space-y-1 ml-2">
                        {perms.map(perm => (
                          <label key={perm.id} className="flex items-start gap-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 p-2 rounded">
                            <input
                              type="checkbox"
                              checked={selectedPerms.includes(perm.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedPerms([...selectedPerms, perm.id]);
                                } else {
                                  setSelectedPerms(selectedPerms.filter(id => id !== perm.id));
                                }
                              }}
                              className="mt-1"
                            />
                            <div className="flex-1 text-sm">
                              <code className="bg-slate-200 dark:bg-slate-700 px-1 py-0.5 rounded text-xs">{perm.code}</code>
                              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">{perm.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* View Mode - Show Permissions */}
            {dialogMode === 'view' && selectedRole && (
              <div>
                <h4 className="font-medium text-sm mb-3">Assigned Permissions ({selectedRole.permissions?.length || 0})</h4>
                <div className="space-y-4 max-h-96 overflow-y-auto border rounded p-3 bg-slate-50 dark:bg-slate-800">
                  {selectedRole.permissions && selectedRole.permissions.length > 0 ? (
                    Object.entries(
                      selectedRole.permissions.reduce((acc, perm) => {
                        if (!acc[perm.module]) acc[perm.module] = [];
                        acc[perm.module].push(perm);
                        return acc;
                      }, {})
                    ).map(([module, perms]) => (
                      <div key={module}>
                        <h5 className="font-medium text-sm text-slate-900 dark:text-white mb-2 capitalize">{module}</h5>
                        <div className="space-y-1 ml-2">
                          {perms.map(perm => (
                            <div key={perm.id} className="text-sm p-2 bg-white dark:bg-slate-900 rounded">
                              <code className="bg-slate-200 dark:bg-slate-700 px-1 py-0.5 rounded text-xs">{perm.code}</code>
                              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">{perm.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">No permissions assigned</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Dialog Actions */}
          <div className="flex justify-end gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Close
            </Button>
            {dialogMode === 'edit' && (
              <Button onClick={handleSaveRole}>
                Save Changes
              </Button>
            )}
            {dialogMode === 'create' && (
              <Button onClick={handleSaveRole}>
                Create Role
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {loading && (
        <div className="text-center py-8 text-slate-500">
          Loading roles...
        </div>
      )}
    </div>
  );
}
